export const UPDATE_PROMPT = 'aiPrompting/UPDATE_PROMPT';
export const UPDATE_CHAT = 'aiPrompting/UPDATE_CHAT';
export const RESET_CAMPAIGN = 'aiPrompting/RESET_CAMPAIGN';
export const CLEAR_STATE = 'aiPrompting/CLEAR_STATE';

// Campaign "Create" (generate:true).
export const CREATE_AI_PROMPT_REQUESTED = 'create/CREATE_AI_PROMPT_REQUESTED';
export const CREATE_AI_PROMPT_SUCCESS = 'create/CREATE_AI_PROMPT_SUCCESS';
export const CREATE_AI_PROMPT_FAILURE = 'create/CREATE_AI_PROMPT_FAILURE';

// Q&A chat widget (generate:false).
export const CHAT_REQUESTED = 'chat/CHAT_REQUESTED';
export const CHAT_SUCCESS = 'chat/CHAT_SUCCESS';
export const CHAT_FAILURE = 'chat/CHAT_FAILURE';

// Auto-rule creation (second step after the template is created).
export const RULE_REQUESTED = 'rule/RULE_REQUESTED';
export const RULE_SUCCESS = 'rule/RULE_SUCCESS';
export const RULE_FAILURE = 'rule/RULE_FAILURE';

// Async job polling + fetching the saved template JSON.
export const JOB_REQUESTED = 'job/JOB_REQUESTED';
export const JOB_SUCCESS = 'job/JOB_SUCCESS';
export const JOB_FAILURE = 'job/JOB_FAILURE';
export const TEMPLATE_JSON_REQUESTED = 'templateJson/TEMPLATE_JSON_REQUESTED';
export const TEMPLATE_JSON_SUCCESS = 'templateJson/TEMPLATE_JSON_SUCCESS';
export const TEMPLATE_JSON_FAILURE = 'templateJson/TEMPLATE_JSON_FAILURE';

// Conversational template optimisation (edit) + single-level undo.
export const EDIT_TEMPLATE_REQUESTED = 'edit/EDIT_TEMPLATE_REQUESTED';
export const EDIT_TEMPLATE_SUCCESS = 'edit/EDIT_TEMPLATE_SUCCESS';
export const EDIT_TEMPLATE_FAILURE = 'edit/EDIT_TEMPLATE_FAILURE';
export const UNDO_TEMPLATE_REQUESTED = 'undo/UNDO_TEMPLATE_REQUESTED';
export const UNDO_TEMPLATE_SUCCESS = 'undo/UNDO_TEMPLATE_SUCCESS';
export const UNDO_TEMPLATE_FAILURE = 'undo/UNDO_TEMPLATE_FAILURE';

const emptyFields = {
  startDate: null,
  endDate: null,
  startDateAt: null, // precise epoch ms for time-based start ("now" / "5 mins")
  endDateAt: null,
  templateType: null,
  quadrantCount: null,
};

// Campaign-flow fields (reset by RESET_CAMPAIGN; the chat widget is untouched).
const campaignState = {
  prompt: '',
  phase: 'input', // 'input' | 'chatting' | 'done'
  messages: [], // campaign conversation { role: 'user' | 'bot', text }
  fields: { ...emptyFields },
  initialFields: {}, // fields present in the first prompt (so they aren't re-stated)
  pendingField: null,
  config: null,
  creating: false,
  created: false,
  previewData: null, // saved template -> wheel-preview data prop
  templateStatus: null, // null | 'creating' | 'done' — template (AI) step
  ruleStatus: null, // null | 'creating' | 'done' | 'error' — auto-rule step
  liveAt: null, // epoch ms when the campaign goes live (start time)
  endAt: null, // epoch ms when the campaign ends (end time), for the studio summary
  ruleName: null, // rule name returned by the auto-rule API (for the play URL)
  templateId: null, // saved template id (for "Edit template")
  ruleId: null, // saved rule id (for "Edit campaign")
  // Review / optimise stage (after preview, before the campaign is created).
  conversationId: null, // AI edit conversation id (from the first edit's job-check)
  editStatus: null, // null | 'editing' | 'error' — optimise-design step
  undoStatus: null, // null | 'undoing' | 'error' — revert-last-change step
  canUndo: false, // a template edit is available to revert (single-level undo)
  previewKey: 0, // bumped to force the wheel preview to re-render after edit/undo
};

const initialState = {
  ...campaignState,
  // Independent floating Q&A chat widget.
  chatOpen: false,
  chatMessages: [], // { role: 'user' | 'bot', text }
  chatBusy: false, // true while a chat reply is in flight
};

export default (state = initialState, action) => {
  switch (action.type) {
    case UPDATE_PROMPT: {
      return { ...state, prompt: action.payload };
    }
    case UPDATE_CHAT: {
      return { ...state, ...action.payload };
    }
    case CREATE_AI_PROMPT_REQUESTED: {
      return { ...state, creating: true };
    }
    // created/previewData are set by the container's handleCreate.
    case CREATE_AI_PROMPT_SUCCESS:
    case CREATE_AI_PROMPT_FAILURE: {
      return { ...state, creating: false };
    }
    case CHAT_REQUESTED: {
      return { ...state, chatBusy: true };
    }
    case CHAT_SUCCESS:
    case CHAT_FAILURE: {
      return { ...state, chatBusy: false };
    }
    case RESET_CAMPAIGN: {
      // Reset the campaign flow only; leave the chat widget as-is.
      return { ...state, ...campaignState, fields: { ...emptyFields } };
    }
    case CLEAR_STATE: {
      return { ...initialState, fields: { ...emptyFields } };
    }
    default:
      return state;
  }
};

// actions
export const updatePrompt = payload => ({ type: UPDATE_PROMPT, payload });

// Merge any subset of state (messages, fields, phase, chatMessages, chatOpen, …).
export const updateChat = payload => ({ type: UPDATE_CHAT, payload });

export const resetCampaign = () => ({ type: RESET_CAMPAIGN });

export const clearState = () => ({ type: CLEAR_STATE });

// Campaign create — generate: true. `extra` adds startDateTime/endDateTime (ms).
export const createAIPrompt = (prompt, extra = {}) => ({
  types: [CREATE_AI_PROMPT_REQUESTED, CREATE_AI_PROMPT_SUCCESS, CREATE_AI_PROMPT_FAILURE],
  promise: client => client.post('aiGenerateResponse', { data: { prompt, generate: true, ...extra } }),
});

// Q&A chat question — generate: false.
export const askAIQuestion = prompt => ({
  types: [CHAT_REQUESTED, CHAT_SUCCESS, CHAT_FAILURE],
  promise: client => client.post('aiGenerateResponse', { data: { prompt, generate: false } }),
});

// Second step: create the auto-rule for the campaign (dummy endpoint for now).
export const createAutoRule = payload => ({
  types: [RULE_REQUESTED, RULE_SUCCESS, RULE_FAILURE],
  promise: client => client.post('createAutoRule', { data: { generate: true, ...payload } }),
});

// Poll the async job started by generate/response (returns status + templateId).
export const checkAiJob = jobId => ({
  types: [JOB_REQUESTED, JOB_SUCCESS, JOB_FAILURE],
  promise: client => client.get(`checkAiJob?jobId=${jobId}`),
});

// Fetch the saved template JSON for preview (same shape used by the edit flow).
export const getTemplateJson = gamificationId => ({
  types: [TEMPLATE_JSON_REQUESTED, TEMPLATE_JSON_SUCCESS, TEMPLATE_JSON_FAILURE],
  promise: client => client.get(`getSpinWheel?gamificationId=${gamificationId}`),
});

// Optimise the generated template via a conversational edit — generate: true.
// `conversationId` is included from the 2nd turn onward (obtained from the
// first edit's job-check). Async: returns 202 + jobId, polled via checkAiJob.
export const editTemplate = payload => ({
  types: [EDIT_TEMPLATE_REQUESTED, EDIT_TEMPLATE_SUCCESS, EDIT_TEMPLATE_FAILURE],
  promise: client => client.post('editTemplate', { data: { generate: true, ...payload } }),
});

// Revert the last template edit (single-level undo). Synchronous: 200 = reverted
// (re-fetch the preview), 204 = nothing to undo. Only templateId (= gamificationId)
// is passed as a request param; no conversationId, no job polling.
export const undoTemplate = templateId => ({
  types: [UNDO_TEMPLATE_REQUESTED, UNDO_TEMPLATE_SUCCESS, UNDO_TEMPLATE_FAILURE],
  promise: client => client.post(`undoTemplate?templateId=${templateId}`),
});
